import {
  collection,
  doc,
  setDoc,
  updateDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
  enableNetwork,
  disableNetwork,
  onSnapshot,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  TestAttempt,
  TestAttemptDocument,
  TestResultDocument
} from '../types/testy';

export class ProgressStore {
  private static instance: ProgressStore;
  private offlineQueue: Array<() => Promise<void>> = [];
  private isOnline = true;

  private constructor() {
    // Monitor online/offline status
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));
  }

  public static getInstance(): ProgressStore {
    if (!ProgressStore.instance) {
      ProgressStore.instance = new ProgressStore();
    }
    return ProgressStore.instance;
  }

  private handleOnline(): void {
    this.isOnline = true;
    enableNetwork(db);
    this.processOfflineQueue();
  }

  private handleOffline(): void {
    this.isOnline = false;
    disableNetwork(db);
  }

  private async processOfflineQueue(): Promise<void> {
    while (this.offlineQueue.length > 0) {
      const operation = this.offlineQueue.shift();
      if (operation) {
        try {
          await operation();
        } catch (error) {
          console.error('Failed to process queued operation:', error);
          // Re-queue the operation if it fails
          this.offlineQueue.unshift(operation);
          break;
        }
      }
    }
  }

  /**
   * Recursively remove undefined values from an object to make it Firestore-compatible
   * @param obj Object to clean
   * @returns Object with undefined values removed
   */
  private removeUndefinedValues(obj: any): any {
    if (obj === null || obj === undefined) {
      return null;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.removeUndefinedValues(item));
    }
    
    if (typeof obj === 'object') {
      const cleaned: any = {};
      for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined) {
          cleaned[key] = this.removeUndefinedValues(value);
        }
      }
      return cleaned;
    }
    
    return obj;
  }

  private convertToFirestoreDocument(attempt: TestAttempt): TestAttemptDocument {
    const doc: TestAttemptDocument = {
      id: attempt.id || '',
      testId: attempt.testId,
      userId: attempt.userId,
      startTime: Timestamp.fromDate(attempt.startTime),
      timeRemaining: attempt.timeRemaining,
      answers: attempt.answers,
      flagged: Array.from(attempt.flagged),
      isCompleted: attempt.isCompleted,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Only include endTime if it exists
    if (attempt.endTime) {
      doc.endTime = Timestamp.fromDate(attempt.endTime);
    }

    // Only include scores if they exist, and filter out undefined values
    if (attempt.scores) {
      doc.scores = this.removeUndefinedValues(attempt.scores);
    }

    return doc;
  }

  private convertFromFirestoreDocument(doc: TestAttemptDocument): TestAttempt {
    return {
      id: doc.id,
      testId: doc.testId,
      userId: doc.userId,
      startTime: doc.startTime.toDate(),
      endTime: doc.endTime ? doc.endTime.toDate() : undefined,
      timeRemaining: doc.timeRemaining,
      answers: doc.answers,
      flagged: new Set(doc.flagged),
      isCompleted: doc.isCompleted,
      scores: doc.scores
    };
  }

  /**
   * Save test attempt progress to Firestore
   * Implements auto-save with offline queue support
   */
  public async saveProgress(attempt: TestAttempt): Promise<TestAttempt> {
    // Track if this is a new document (no ID initially)
    const isNewDocument = !attempt.id;
    
    // Generate ID if not present
    if (!attempt.id) {
      attempt.id = this.generateAttemptId(attempt.testId, attempt.userId);
    }

    const operation = async () => {
      try {
        const attemptRef = doc(db, 'testAttempts', attempt.id!);
        
        const firestoreDoc = this.convertToFirestoreDocument(attempt);

        // Use setDoc for new documents, updateDoc for existing ones
        if (isNewDocument) {
          await setDoc(attemptRef, firestoreDoc);
        } else {
          await updateDoc(attemptRef, {
            ...firestoreDoc,
            updatedAt: serverTimestamp()
          });
        }
      } catch (error) {
        console.error('Failed to save progress:', error);
        throw error;
      }
    };

    if (this.isOnline) {
      await operation();
    } else {
      // Queue the operation for when we're back online
      this.offlineQueue.push(operation);
    }
    
    return attempt;
  }

  /**
   * Load test attempt progress from Firestore
   * Returns null if no attempt exists
   */
  public async loadProgress(testId: string, userId: string): Promise<TestAttempt | null> {
    try {
      // Query for incomplete attempts (simplified to avoid index requirement)
      const attemptsRef = collection(db, 'testAttempts');
      const q = query(
        attemptsRef,
        where('testId', '==', testId),
        where('userId', '==', userId),
        where('isCompleted', '==', false)
      );

      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        // Sort by startTime in memory to get the most recent
        const attempts = querySnapshot.docs
          .map(doc => {
            const data = doc.data() as TestAttemptDocument;
            return this.convertFromFirestoreDocument({ ...data, id: doc.id });
          })
          .sort((a, b) => b.startTime.getTime() - a.startTime.getTime());
        
        return attempts[0];
      }

      return null;
    } catch (error) {
      console.error('Failed to load progress:', error);
      throw error;
    }
  }

  /**
   * Submit final test results and mark attempt as completed
   */
  public async submitFinalResults(attempt: TestAttempt): Promise<void> {
    const operation = async () => {
      try {
        if (!attempt.id) {
          throw new Error('Cannot submit results for attempt without ID');
        }

        // Update the test attempt as completed
        const attemptRef = doc(db, 'testAttempts', attempt.id);
        const completedAttempt = {
          ...attempt,
          isCompleted: true,
          endTime: new Date()
        };

        const firestoreDoc = this.convertToFirestoreDocument(completedAttempt);
        await updateDoc(attemptRef, {
          ...firestoreDoc,
          updatedAt: serverTimestamp()
        });

        // Create a separate test result document for easier querying
        if (attempt.scores) {
          const resultId = `${attempt.testId}_${attempt.userId}_${Date.now()}`;
          const resultRef = doc(db, 'testResults', resultId);
          
          const timeSpent = attempt.startTime && completedAttempt.endTime 
            ? Math.floor((completedAttempt.endTime.getTime() - attempt.startTime.getTime()) / 1000)
            : 0;

          const resultDoc: TestResultDocument = {
            id: resultId,
            testId: attempt.testId,
            userId: attempt.userId,
            attemptId: attempt.id,
            scores: this.removeUndefinedValues(attempt.scores),
            completedAt: Timestamp.fromDate(completedAttempt.endTime),
            timeSpent
          };

          await setDoc(resultRef, resultDoc);
        }
      } catch (error) {
        console.error('Failed to submit final results:', error);
        throw error;
      }
    };

    if (this.isOnline) {
      await operation();
    } else {
      this.offlineQueue.push(operation);
    }
  }

  /**
   * Get test history for a user
   */
  public async getTestHistory(userId: string): Promise<TestAttempt[]> {
    try {
      const attemptsRef = collection(db, 'testAttempts');
      const q = query(
        attemptsRef,
        where('userId', '==', userId),
        where('isCompleted', '==', true)
      );

      const querySnapshot = await getDocs(q);
      const attempts: TestAttempt[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data() as TestAttemptDocument;
        attempts.push(this.convertFromFirestoreDocument({ ...data, id: doc.id }));
      });

      // Sort by endTime in memory
      return attempts.sort((a, b) => {
        if (!a.endTime || !b.endTime) return 0;
        return b.endTime.getTime() - a.endTime.getTime();
      });
    } catch (error) {
      console.error('Failed to get test history:', error);
      throw error;
    }
  }

  /**
   * Subscribe to real-time updates for a test attempt
   */
  public subscribeToProgress(
    testId: string, 
    userId: string, 
    callback: (attempt: TestAttempt | null) => void
  ): () => void {
    const attemptsRef = collection(db, 'testAttempts');
    const q = query(
      attemptsRef,
      where('testId', '==', testId),
      where('userId', '==', userId),
      where('isCompleted', '==', false)
    );

    return onSnapshot(q, (querySnapshot) => {
      if (!querySnapshot.empty) {
        // Sort by startTime in memory to get the most recent
        const attempts = querySnapshot.docs
          .map(doc => {
            const data = doc.data() as TestAttemptDocument;
            return this.convertFromFirestoreDocument({ ...data, id: doc.id });
          })
          .sort((a, b) => b.startTime.getTime() - a.startTime.getTime());
        
        callback(attempts[0]);
      } else {
        callback(null);
      }
    }, (error) => {
      console.error('Error in progress subscription:', error);
      callback(null);
    });
  }

  /**
   * Generate a unique attempt ID
   */
  private generateAttemptId(testId: string, userId: string): string {
    const timestamp = Date.now();
    return `${testId}_${userId}_${timestamp}`;
  }

  /**
   * Check if the service is currently online
   */
  public isServiceOnline(): boolean {
    return this.isOnline;
  }

  /**
   * Get the number of queued operations
   */
  public getQueuedOperationsCount(): number {
    return this.offlineQueue.length;
  }

  /**
   * Manually trigger processing of offline queue (useful for testing)
   */
  public async processQueue(): Promise<void> {
    await this.processOfflineQueue();
  }
}

// Export singleton instance
export const progressStore = ProgressStore.getInstance();